import { Interface } from '@ethersproject/abi'
import { BigNumber } from '@ethersproject/bignumber'
import { ChainId, DELAY_ABI, DELAY_ADDRESS, EXECUTE_DEPOSIT_GAS_LIMIT, NATIVE_CURRENCY, toToken } from '../constants'
import { CurrencyValue, Pair } from '../model'
import { Settings } from '../providers'
import { DepositInfo } from '../providers/transactions/model'
import { getAddLiquidityFeeDisplay, areTokensSorted, makeFloatEncodable } from '../utils'

export function getAddLiquidityTransaction(
  account: string,
  chainId: ChainId,
  firstValue: CurrencyValue,
  secondValue: CurrencyValue,
  gasPrice: BigNumber,
  settings: Settings,
  expectedLiquidity: string,
  pair: Pair,
  maxFeePerGasForEthMax?: BigNumber,
  maxGasTipForEthMax?: BigNumber
) {
  const tokenA = toToken(firstValue.currency)
  const tokenB = toToken(secondValue.currency)
  const [token0, token1, currencyValue0, currencyValue1] = areTokensSorted(tokenA, tokenB)
    ? [tokenA, tokenB, firstValue, secondValue]
    : [tokenB, tokenA, secondValue, firstValue]
  const [value0, value1] = [currencyValue0.value, currencyValue1.value]
  const wrap = firstValue.currency === NATIVE_CURRENCY[chainId] || secondValue.currency === NATIVE_CURRENCY[chainId]
  const etherValue =
    firstValue.currency === NATIVE_CURRENCY[chainId]
      ? firstValue.value
      : secondValue.currency === NATIVE_CURRENCY[chainId]
      ? secondValue.value
      : 0
  const safeGasLimit = EXECUTE_DEPOSIT_GAS_LIMIT + settings.gasLimitOverpay
  const nowSeconds = Math.floor(Date.now() / 1000)
  const safeGasPrice = gasPrice.mul(settings.gasPriceMultiplier).div(1000)
  const gasPrepay = safeGasPrice.mul(safeGasLimit)
  const initialPrice = pair.getPrice()
  const txInfo = {
    type: 'DEPOSIT',
    currency0: token0.serialize(),
    currency1: token1.serialize(),
    value0: currencyValue0.format(),
    value1: currencyValue1.format(),
    expectedLiquidity,
    gasPrepay: gasPrepay.toString(),
    slippageTolerance: settings.slippageTolerance.toString(),
    fees: getAddLiquidityFeeDisplay(pair),
  } as DepositInfo
  return {
    to: DELAY_ADDRESS[chainId],
    data: new Interface(DELAY_ABI).encodeFunctionData('deposit', [
      {
        token0: token0.address,
        token1: token1.address,
        amount0: value0,
        amount1: value1,
        initialRatio: pair.getInitialRatio(),
        minRatioChangeToSwap: BigNumber.from(10 * settings.minRatioChange),
        minSwapPrice: makeFloatEncodable(initialPrice.mul(10000 - settings.depositSlippageInBasisPoints).div(10000)),
        maxSwapPrice: makeFloatEncodable(initialPrice.mul(10000 + settings.depositSlippageInBasisPoints).div(10000)),
        wrap,
        to: account,
        gasLimit: safeGasLimit,
        submitDeadline: nowSeconds + settings.submitDeadline,
        executionDeadline: nowSeconds + settings.executionDeadline,
      },
    ]),
    value: gasPrepay.add(etherValue),
    txInfo,
    maxFeePerGas: maxFeePerGasForEthMax,
    maxPriorityFeePerGas: maxGasTipForEthMax,
  }
}
